import React from 'react';
import {
  Box,
  MessageModalLayout,
  Modal,
  SectionHelper,
  Text,
  TextButton,
} from '@wix/design-system';
import { useExperiments } from '@wix/wix-experiments-react';
import { useTranslation } from '../../../hooks/use-translation';
import { useHttpClient } from '../../../hooks/use-http-client';

export type DeletePremiumModalProps = {
  appId: string;
  appName: string;
  handleClose(): void;
  msId: string;
};

export function DeletePremiumModal({
  appId,
  appName,
  handleClose,
  msId,
}: DeletePremiumModalProps) {
  const { experiments } = useExperiments();
  const t = useTranslation();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isPlanChangeEnabled, setIsPlanChangeEnabled] = React.useState(false);
  const httpClient = useHttpClient();

  function openPremiumSubscriptions() {
    const premiumSubscriptionsPageUrl = `https://manage.wix.com/dashboard/${msId}/manage-website/plans`;
    window.open(premiumSubscriptionsPageUrl, '_blank');
    handleClose();
  }

  function openPlanChange() {
    const planChangePageUrl = `https://manage.wix.com/upgrade/app/${appId}/plan-change?meta-site-id=${msId}&origin=manage_apps_bizmgr`;
    window.open(planChangePageUrl, '_blank');
  }

  React.useEffect(() => {
    httpClient
      .get(
        `https://${window.location.hostname}/_serverless/managed-installed-apps-serverless/plan-change?appId=${appId}&metaSiteId=${msId}`,
      )
      .then((response) => response.data.isPlanChangeEnabled)
      .then(setIsPlanChangeEnabled)
      .finally(() => setIsLoading(false));
  }, [appId, msId]);

  if (isLoading) {
    return null;
  }

  return (
    <Modal isOpen>
      <MessageModalLayout
        title={t('manage-apps.delete-premium.header', {
          name: appName,
        }).replace('&amp;', '&')}
        primaryButtonText={t('manage-apps.delete-premium.cta')}
        primaryButtonOnClick={openPremiumSubscriptions}
        secondaryButtonText={t('manage-apps.delete-premium.cancel')}
        secondaryButtonOnClick={handleClose}
        onCloseButtonClick={handleClose}
        dataHook="delete-premium-app-modal"
      >
        <Box direction="vertical" gap="24px" width="552px">
          <Text>
            {t('manage-apps.delete-premium.line1')}
            {t('manage-apps.delete-premium.line2')}
            <Box inline marginLeft="6px">
              <TextButton
                as="a"
                href={t('manage-apps.delete-premium-learn-more-link')}
                target="_blank"
                onClick={handleClose}
              >
                {t('manage-apps.delete-premium.side-action')}
              </TextButton>
            </Box>
          </Text>

          {experiments.enabled('specs.app-market.TpaPlanDowngrade') &&
          isPlanChangeEnabled ? (
            <SectionHelper
              fullWidth={true}
              appearance="standard"
              title={t('manage-apps.delete-modal.change-plan-title')}
              actionText={t(
                'manage-apps.delete-modal.change-plan-button-label',
              )}
              onAction={openPlanChange}
            >
              {t('manage-apps.delete-modal.change-plan-description')}
            </SectionHelper>
          ) : null}
        </Box>
      </MessageModalLayout>
    </Modal>
  );
}
